import React from 'react';
import PropTypes from 'prop-types';
import { getColorForValue } from '../../Utils/ColorUtils';
import CustomTooltip from '../../Utils/CustomTooltip';

// Soft Skill progress bar component with evidence
const SoftSkillProgressBar = ({ skill, score, rationale, evidence, source, showSource = true }) => {
  // Ensure score is between 0 and 10
  const numericScore = Math.min(10, Math.max(0, score));
  
  // Calculate width percentage
  const widthPercentage = (numericScore / 10) * 100;
  
  // Get color based on score
  const barColor = getColorForValue(numericScore);
  
  return (
    <div className="mb-6 border border-gray-200 rounded-lg p-4">
      <div className="flex justify-between items-center mb-2">
        <h5 className="text-lg font-medium text-gray-800">{skill}</h5>
        <div className="flex items-center">
          <span className="text-gray-700 font-medium mr-2">{numericScore}</span>
          {showSource && source && Array.isArray(source) && source.length > 0 && (
            <CustomTooltip content={source.join(', ')} position="left">
              <span className="text-xs text-blue-600 cursor-help hover:text-blue-800 flex items-center">
                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                Source
              </span>
            </CustomTooltip>
          )}
        </div>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-4 mb-2">
        <div 
          className="h-4 rounded-full" 
          style={{ 
            width: `${widthPercentage}%`,
            backgroundColor: barColor
          }}
        ></div>
      </div>
      
      {/* Rationale section */}
      {rationale && (
        <div className="mb-2">
          <h6 className="text-sm font-medium text-gray-700 mb-1">Rationale:</h6>
          <p className="text-sm text-gray-600">{rationale}</p>
        </div>
      )}
      
      {/* Evidence section */}
      {evidence && Array.isArray(evidence) && evidence.length > 0 && (
        <div>
          <h6 className="text-sm font-medium text-gray-700 mb-1">Evidence:</h6>
          <ul className="list-disc pl-5 space-y-1">
            {evidence.map((item, index) => (
              <li key={index} className="text-sm text-gray-600">{item}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const SoftSkillsList = ({ skills, showSources = true }) => {
  if (!skills || !Array.isArray(skills) || skills.length === 0) {
    return <p className="text-gray-600">No soft skills data available.</p>;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {skills.map((skill, index) => (
        <SoftSkillProgressBar
          key={index}
          skill={skill.skill}
          score={skill.score || skill.grade} //BACKWARDS COMPATIBILITY
          rationale={skill.rationale}
          evidence={skill.evidence}
          source={skill.source}
          showSource={showSources}
        />
      ))}
    </div>
  );
};

SoftSkillsList.propTypes = {
  skills: PropTypes.array,
  showSources: PropTypes.bool
};

export default SoftSkillsList;
